import { useEffect, useState } from 'react';
import { 
  AlertTriangle, 
  Clock, 
  Phone, 
  Mail, 
  Calendar,
  CheckCircle2,
  Settings,
  Eye
} from 'lucide-react';
import { useStore } from '../lib/store';
import { formatDate, formatPhone, cn } from '../lib/utils';
import LeadDetail from '../components/LeadDetail';

export default function Alerts() {
  const { leads, alerts, selectedLead, selectLead, fetchLeads, fetchAlerts, updateLead } = useStore();
  const [thresholdDays, setThresholdDays] = useState(7);
  
  useEffect(() => {
    fetchLeads().then(() => {
      fetchAlerts(thresholdDays);
    });
  }, [thresholdDays]);
  
  const handleSetFollowup = async (leadId: string, date: string) => {
    await updateLead(leadId, { next_followup: date });
  };
  
  const getUrgencyStyle = (days: number) => {
    if (days >= 14) return {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      badge: 'bg-red-100 text-red-700'
    };
    if (days >= 10) return {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      badge: 'bg-amber-100 text-amber-700'
    };
    return {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-700',
      badge: 'bg-yellow-100 text-yellow-700'
    };
  };
  
  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Relances</h1>
          <p className="text-gray-500 mt-1">Leads nécessitant une relance</p>
        </div>
        
        <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-2">
          <Settings className="w-4 h-4 text-gray-400" />
          <label className="text-sm text-gray-600">Seuil:</label>
          <select
            value={thresholdDays}
            onChange={e => setThresholdDays(Number(e.target.value))}
            className="border-0 text-sm font-medium text-gray-900 focus:ring-0 outline-none bg-transparent cursor-pointer"
          >
            <option value={3}>3 jours</option>
            <option value={5}>5 jours</option>
            <option value={7}>7 jours</option>
            <option value={10}>10 jours</option>
            <option value={14}>14 jours</option>
          </select>
        </div>
      </div>
      
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-900">
                {alerts.filter(a => a.daysSinceContact >= 14).length}
              </p>
              <p className="text-sm text-red-600">Critiques (+14j)</p>
            </div>
          </div>
        </div>
        
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-900">
                {alerts.filter(a => a.daysSinceContact >= 10 && a.daysSinceContact < 14).length}
              </p>
              <p className="text-sm text-amber-600">À risque (10-14j)</p>
            </div>
          </div>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-900">
                {alerts.filter(a => a.daysSinceContact < 10).length}
              </p>
              <p className="text-sm text-yellow-600">À surveiller ({thresholdDays}-10j)</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Alerts List */}
      {alerts.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7 text-green-500" />
          </div>
          <h2 className="text-lg font-semibold text-green-900 mb-2">Tout est à jour !</h2>
          <p className="text-green-600 text-sm">
            Aucun lead n'a besoin de relance avec le seuil actuel de {thresholdDays} jours.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map(({ lead, daysSinceContact }) => {
            const style = getUrgencyStyle(daysSinceContact);
            return (
              <div 
                key={lead.id}
                className={cn(
                  'border rounded-xl p-4',
                  style.bg,
                  style.border
                )}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">
                        {lead.name || lead.company || 'Sans nom'}
                      </h3>
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        style.badge
                      )}>
                        {daysSinceContact}j sans contact
                      </span>
                    </div>
                    
                    {lead.activity && (
                      <p className="text-sm text-gray-600 mb-3">{lead.activity}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm flex-wrap">
                      {lead.email && (
                        <a 
                          href={`mailto:${lead.email}`}
                          className="flex items-center gap-1.5 text-gray-700 hover:text-primary-600 transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          <span className="truncate max-w-[200px]">{lead.email}</span>
                        </a>
                      )}
                      {lead.phone && (
                        <a 
                          href={`tel:${lead.phone}`}
                          className="flex items-center gap-1.5 text-gray-700 hover:text-green-600 transition-colors"
                        >
                          <Phone className="w-4 h-4" />
                          {formatPhone(lead.phone)}
                        </a>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => selectLead(lead)}
                      className="flex items-center gap-2 px-3 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium border border-gray-200"
                    >
                      <Eye className="w-4 h-4" />
                      Détails
                    </button>
                    
                    <div className="relative">
                      <input
                        type="date"
                        onChange={e => handleSetFollowup(lead.id, e.target.value)}
                        className="px-3 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm border border-gray-200 cursor-pointer"
                        title="Planifier une relance"
                      />
                    </div>
                  </div>
                </div>
                
                {lead.next_followup && (
                  <div className="mt-3 pt-3 border-t border-white/50 flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>
                      Relance prévue : {formatDate(lead.next_followup, "EEEE d MMMM yyyy")}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {selectedLead && (
        <LeadDetail 
          lead={selectedLead} 
          onClose={() => selectLead(null)} 
        />
      )}
    </div>
  );
}
